import { useGomakeAuth, useGomakeRouter } from "@/hooks";
import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { companyProfileState, ICompanyProfile } from "@/store/company-profile";
import { aiCreditsNavAlertState } from "@/store/ai-credits-nav";
import { Permissions } from "@/components/CheckPermission/enum";
import { HomeIcon } from "@/icons/side-list-icons/home";
import { ProductionFloorIcon } from "@/icons/side-list-icons/production-floor";
import { SalesIcon } from "@/icons/side-list-icons/sales";
import { PurchaseIcon } from "@/icons/side-list-icons/purchase";
import { BankingIcon } from "@/icons/side-list-icons/banking";
import { ContactsIcon } from "@/icons/side-list-icons/contacts";
import { ReportsIcon } from "@/icons/side-list-icons/reports";
import {
  MaterialsAdminIcon,
  MaterialsIcon,
} from "@/icons/side-list-icons/material";
import {
  MachinesAdminIcon,
  MachinesIcon,
} from "@/icons/side-list-icons/machines";
import { ActionsIcon } from "@/icons/side-list-icons/actions";
import { OutputsAdminIcon } from "@/icons/side-list-icons/outputs";
import { ActionsAdminIcon } from "@/icons/side-list-icons/actions-admin";
import { SettingsIcon } from "@/icons/side-list-icons/settings";
import { CustomerServiceIcon } from "@/icons/side-list-icons/customer-service";
import { AIIcon } from "@/icons/side-list-icons/ai";
import { DashboardIcon } from "@/icons/side-list-icons/dashboard";
import { HpIndigoIcon } from "@/icons/side-list-icons/hp-indigo";
import { getUserToken } from "@/services/storage-data";
import { useTheme } from "@/contexts/theme";
import { startHostedLogout } from "@/utils/hosted-login";

const useAuthLayoutHook = (
  permissionEnumValue?: Permissions,
  allowAnonymous?: boolean,
) => {
  const { isAuth, isLanguageUpdated } = useGomakeAuth(
    permissionEnumValue,
    allowAnonymous,
  );
  const { navigate } = useGomakeRouter();
  const { currentTheme } = useTheme();
  const [canAccess, setCanAccess] = useState<boolean | null>(null);
  const profile = useRecoilValue<ICompanyProfile>(companyProfileState);
  const showAiCreditsAlert = useRecoilValue(aiCreditsNavAlertState);
  const isHpClient = currentTheme === "hp-client";

  const handleLogout = () => {
    // Ends the Cognito Hosted UI session as well. A local-only logout leaves that
    // session in place, so /login silently signs the user back in.
    void startHostedLogout();
  };

  const tabs1: any = useMemo(() => {
    return [
      {
        isLine: false,
        key: "home",
        title: "tabs.home",
        path: "/home",
        isList: false,
        icon: () => {
          return <HomeIcon />;
        },
        isProduction: true,
        tourData: "menu-home",
      },
      {
        isLine: false,
        key: "goAI",
        title: isHpClient ? "tabs.AtlasAi" : "tabs.GoAi",
        path: "/gomake-ai",
        Permission: Permissions.SHOW_GOMAKE_AI,
        isList: false,
        icon: () => {
          return <AIIcon />;
        },
        isProduction: true,
        tourData: "menu-go-ai",
      },
      {
        isLine: false,
        key: "aiDashboard",
        title: "tabs.aiDashboard",
        path: "/gomake-ai/dashboard",
        Permission: Permissions.SHOW_AI_DASHBOARD,
        isList: false,
        icon: () => {
          return <DashboardIcon color={isHpClient ? "#000" : "#fff"} />;
        },
        isProduction: true,
        tourData: "menu-ai-dashboard",
        showRedPoint: showAiCreditsAlert,
      },
      {
        isLine: false,
        key: "productFloor",
        title: "tabs.productFloor",
        path: "/production-floor?view=table",
        isList: false,
        Permission: Permissions.SHOW_PRODUCTION_FLOOR,
        icon: () => {
          return <ProductionFloorIcon />;
        },
        isProduction: true,
      },
    ];
  }, [isHpClient, showAiCreditsAlert]);

  const tabs2: any = useMemo(() => {
    return [
      {
        isLine: true,
        key: "line_1",
        isProduction: true,
      },
      {
        isLine: false,
        key: "sales",
        title: "tabs.sales",
        path: "/",
        isList: true,
        list: [
          {
            key: "jobs",
            title: "home.tabs.jobs",
            Permission: Permissions.SHOW_BOARD_MISSIONS,
            path: "/jobs",
          },
          {
            key: "quotes",
            title: "tabs.quotes",
            path: "/quotes",
            Permission: Permissions.SHOW_QUOTES,
          },
          {
            key: "orders",
            title: "tabs.orders",
            path: "/orders",
            Permission: Permissions.SHOW_ORDERS,
          },
          {
            key: "customerSku",
            title: "settings.customerSku",
            path: "/customer-sku",
            Permission: Permissions.SHOW_CUSTOMER_SKU,
          },
          {
            key: "delivery notes",
            title: "tabs.deliveryNotes",
            path: "/deliveryNotes",
            Permission: Permissions.SHOW_DELIVERY_NOTES,
          },
          {
            key: "delivery note refund",
            title: "tabs.deliveryNoteRefund",
            path: "/deliveryNoteRefunds",
            Permission: Permissions.SHOW_DELIVERY_NOTES_REFUND,
          },
          {
            key: "proforma invoices",
            title: "tabs.proformaInvoices",
            path: "/proformaInvoices",
            Permission: Permissions.SHOW_PROFORMA_INVOICES,
          },
          {
            key: "invoices",
            title: "tabs.invoices",
            path: "/invoices",
            Permission: Permissions.SHOW_INVOICES,
          },
          {
            key: "invoice refund",
            title: "tabs.invoiceRefund",
            path: "/invoiceRefunds",
            Permission: Permissions.SHOW_INVOICES_REFUND,
          },
          {
            key: "deliveries",
            title: "tabs.deliveries",
            path: "/deliveries",
            Permission: Permissions.SHOW_DELIVERIES,
          },
          {
            key: "dashboard",
            title: "tabs.dashboard",
            path: "/dashboard",
            Permission: Permissions.SHOW_SALES_DASHBOARD,
          },
          {
            key: "productionDashboard",
            title: "tabs.productionDashboard",
            path: "/production-dashboard",
            Permission: Permissions.SHOW_SALES_DASHBOARD,
          },
        ],
        icon: () => {
          return <SalesIcon />;
        },
        isProduction: true,
      },
      // {
      //   isLine: false,
      //   key: "properties",
      //   title: "tabs.properties",
      //   path: "/properties",
      //   isList: false,
      //   icon: () => {
      //     return <ProductsIcon />;
      //   },
      //   isProduction: true,
      // },
      {
        isLine: false,
        key: "purchase",
        title: "tabs.purchase",
        path: "/",
        isList: true,
        list: [
          {
            key: "purchaseJobs",
            title: "tabs.purchaseJobs",
            path: "/purchase-jobs",
            Permission: Permissions.SHOW_PURCHASE_INVOICES_REFUND,
          },
          {
            key: "purchase order",
            title: "tabs.purchaseOrder",
            path: "/purchaseOrders",
            Permission: Permissions.SHOW_PURCHASE_ORDERS,
          },
          {
            key: "purchase invoice",
            title: "tabs.purchaseInvoice",
            path: "/purchaseInvoices",
            Permission: Permissions.SHOW_PURCHASE_INVOICES,
          },
          {
            key: "purchase invoice refund",
            title: "tabs.purchaseInvoiceRefund",
            path: "/purchaseInvoiceRefunds",
            Permission: Permissions.SHOW_PURCHASE_INVOICES_REFUND,
          },
        ],
        icon: () => {
          return <PurchaseIcon />;
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "banking",
        title: "tabs.banking",
        path: "/",
        isList: true,
        list: [
          {
            key: "receipts",
            title: "tabs.receipts",
            path: "/receipts",
            Permission: Permissions.SHOW_RECEIPTS,
          },
          {
            key: "payments",
            title: "tabs.supplierPayments",
            path: "/supplier-payments",
            Permission: Permissions.SHOW_SUPPLIER_PAYMENTS,
          },
          {
            key: "deposits",
            title: "tabs.deposits",
            path: "/deposits",
            Permission: Permissions.SHOW_DEPOSITS,
          },
        ],
        icon: () => {
          return <BankingIcon />;
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "contacts",
        title: "tabs.contacts",
        path: "/",
        isList: true,
        list: [
          {
            key: "customers",
            title: "tabs.customers",
            path: "/customers",
            Permission: Permissions.SHOW_CLIENT,
          },
          {
            key: "suppliers",
            title: "tabs.suppliers",
            path: "/suppliers",
            Permission: Permissions.SHOW_SUPPLIER,
          },
          // {
          //   key: "myTickets",
          //   title: "tabs.myTickets",
          //   path: "/my-tickets",
          //   Permission: undefined, // No permission required - all users can see their tickets
          // },
          // {
          //   key: "helpCenter",
          //   title: "tabs.helpCenter",
          //   path: "/help-center",
          //   Permission: undefined, // No permission required - all users can access help center
          // },
        ],
        icon: () => {
          return <ContactsIcon />;
        },
        isProduction: true,
      },

      {
        isLine: false,
        key: "customerReports",
        title: "tabs.customerReports",
        isList: true,
        list: [
          {
            key: "agingReport",
            title: "tabs.agingReport",
            path: "/aging-report",
            Permission: Permissions.SHOW_AGING_REPORT,
          },
          {
            key: "ledgerReport",
            title: "tabs.customerLedger",
            path: "/ledger-report",
            Permission: Permissions.SHOW_LEDGER_REPORT,
          },

          {
            key: "dailyPaymentsReport",
            title: "tabs.receiptsReport",
            path: "/daily-payments-report",
            Permission: Permissions.SHOW_DAILY_PAYMENT_REPORT,
          },
          {
            key: "salesReport",
            title: "tabs.salesReport",
            path: "/sales-report",
            Permission: Permissions.SHOW_SALES_REPORT,
          },
          {
            key: "creditCardTransactions",
            title: "tabs.creditCardTransactions",
            path: "/credit-card-transactions",
            Permission: Permissions.SHOW_CREDIT_CARD_TRANSACTIONS,
          },
          {
            key: "vatReport",
            title: "tabs.vatReport",
            path: "/vat-report",
            Permission: Permissions.SHOW_VAT_REPORT,
          },
          {
            key: "mailingLogs",
            title: "mailingSettings.messageLogs",
            path: "/mailing-logs",
            Permission: Permissions.SHOW_MAILING_LOGS,
          },
          {
            key: "roiReport",
            title: "ROI Report",
            path: "/machines-comparison",
            Permission: Permissions.SHOW_HP_INDIGO, // No permission required - all users can access HP Indigo Report
          },
        ],
        icon: () => {
          return <ReportsIcon />;
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "supplierReports",
        title: "tabs.supplierReports",
        isList: true,
        list: [
          {
            key: "supplierAging",
            title: "tabs.supplierAging",
            path: "/supplier-aging",
            Permission: Permissions.SHOW_SUPPLIER_AGING_REPORT,
          },
          {
            key: "supplierLedger",
            title: "tabs.supplierLedger",
            path: "/supplier-ledger",
            Permission: Permissions.SHOW_SUPPLIER_LEDGER_REPORT,
          },
          {
            key: "paymentsReport",
            title: "tabs.paymentsReport",
            path: "/payments-report",
            Permission: Permissions.SHOW_SUPPLIER_PAYMENT_REPORT,
          },
          {
            key: "purchasesReport",
            title: "tabs.purchasesReport",
            path: "/purchases-report",
            Permission: Permissions.SHOW_PURCHASES_REPORT,
          },
        ],
        icon: () => {
          return <ReportsIcon />;
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "accountingReports",
        title: "tabs.accountingReports",
        isList: true,
        list: [
          // {
          //   key: "transactionJournalReport",
          //   title: "tabs.transactionJournalReport",
          //   path: "/transaction-journal-report",
          //   Permission: Permissions.SHOW_TRANSACTION_JOURNAL_REPORT,
          // },
          {
            key: "accountingTransactionReport",
            title: "tabs.accountingTransactionReport",
            path: "/accounting-transaction-report",
            Permission: Permissions.SHOW_TRANSACTION_JOURNAL_REPORT,
          },
          {
            key: "adjustmentsReport",
            title: "tabs.adjustmentsReport",
            path: "/adjustments-report",
            Permission: Permissions.SHOW_TRANSACTION_JOURNAL_REPORT,
          },
          {
            key: "exportFiles",
            title: "tabs.exportFiles",
            path: "/export-files",
            Permission: Permissions.SHOW_FINANCE,
          },
        ],
        icon: () => {
          return <ReportsIcon />;
        },
        isProduction: true,
      },
    ];
  }, []);

  const tabs3: any = useMemo(() => {
    return [
      {
        isLine: true,
        key: "line_2",
        isProduction: true,
      },
      {
        isLine: false,
        key: "materials",
        title: "tabs.materials",
        Permission: Permissions.SHOW_MATERIALS,
        path: "/materials",
        isList: false,
        icon: () => {
          return <MaterialsIcon />;
        },
        isProduction: true,
        tourData: "menu-materials",
      },
      {
        isLine: false,
        key: "materialsAdmin",
        title: "tabs.materialsAdmin",
        Permission: Permissions.MATERIALS_ADMIN,
        path: "/materials-admin",
        isList: false,
        icon: () => {
          return <MaterialsAdminIcon />;
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "machines",
        title: "tabs.machines",
        path: "/machines",
        Permission: Permissions.SHOW_MACHINES,
        isList: false,
        icon: () => {
          return <MachinesIcon />;
        },
        isProduction: true,
        tourData: "menuMachines",
      },
      {
        isLine: false,
        key: "machinesAdmin",
        title: "tabs.machinesAdmin",
        path: "/machines-admin",
        Permission: Permissions.MACHINES_ADMIN,
        isList: false,
        icon: () => {
          return <MachinesAdminIcon />;
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "outputsAdmin",
        title: "tabs.outputsAdmin",
        path: "/outputs-admin",
        Permission: Permissions.MACHINES_ADMIN,
        isList: false,
        icon: () => {
          return (
            <OutputsAdminIcon
              color={isHpClient ? `var(--theme-combobox-icon-color)` : "#fff"}
            />
          );
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "parametersAdmin",
        title: "tabs.parametersAdmin",
        path: "/parameters-admin",
        Permission: Permissions.MACHINES_ADMIN,
        isList: false,
        icon: () => {
          return (
            <OutputsAdminIcon
              color={isHpClient ? `var(--theme-combobox-icon-color)` : "#fff"}
            />
          );
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "actionsAdmin",
        title: "tabs.actionsAdmin",
        path: "/actions-admin",
        Permission: Permissions.MACHINES_ADMIN,
        isList: false,
        icon: () => {
          return (
            <ActionsAdminIcon
              color={isHpClient ? `var(--theme-combobox-icon-color)` : "#fff"}
            />
          );
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "actions",
        title: "tabs.actions",
        path: "/actions",
        Permission: Permissions.SHOW_ACTIONS,
        isList: false,
        icon: () => {
          return <ActionsIcon />;
        },
        isProduction: true,
        tourData: "menuActions",
      },

      {
        isLine: false,
        key: "companyReports",
        title: "companyReports.printhouseReport",
        path: "/companyReports",
        Permission: Permissions.SHOW_PRINTHOUSES_TABLE,
        isList: false,
        icon: () => {
          return <ReportsIcon />;
        },
        isProduction: true,
        tourData: "menu-companyReports",
      },
    ];
  }, []);

  const tabs4: any = useMemo(() => {
    return [
      {
        isLine: true,
        key: "line_2",
        isProduction: true,
      },
      {
        isLine: false,
        key: "customerService",
        title: "tabs.customerService",
        path: "/customer-service-admin",
        Permission: Permissions.SHOW_ADMIN_CUSTOMER_SERVICE,
        isList: false,
        icon: () => {
          return <CustomerServiceIcon />;
        },
        isProduction: true,
      },
      {
        isLine: false,
        key: "settings",
        title: "tabs.settings",
        path: "/settings",
        isList: false,
        icon: () => {
          return <SettingsIcon />;
        },
        isProduction: true,
        tourData: "menu-settings",
      },
      // Add any other new tabs here as needed
    ];
  }, []);

  useEffect(() => {
    if (allowAnonymous && !getUserToken()) {
      setCanAccess(true);
      return;
    }
    if (isAuth === false) {
      setCanAccess(false);
      return;
    }
    if (isAuth === true && isLanguageUpdated) {
      setCanAccess(true);
    }
  }, [isAuth, isLanguageUpdated, allowAnonymous]);

  const permissionsOfTabs = [tabs1, tabs2, tabs3, tabs4];

  return {
    permissionsOfTabs,
    tabs1,
    tabs2,
    tabs3,
    tabs4,
    canAccess,
    profile,
    navigate,
    handleLogout,
  };
};

export { useAuthLayoutHook };
